import { Box, Card, Loader, Stack, Tooltip, useMantineTheme } from '@mantine/core';
import { exprBuilder, normalizeAndTraverseQuery, queryBuilder, traverseExpr, traverseExprDepthFirst } from '@root/Services/QueryExpr';
import { ColumnConfig, DataSourceConfig, GridGroupByState } from '@root/Components/DataGrid/Models';

import { AssessmentResult, Query } from '@apis/Resources/model';
import { DataGridModel } from '@root/Components/DataGrid/DataGridModel';
import { FormatService } from '@root/Services/FormatService';
import { CompanyStat, IQueryExpr } from '@apis/Customers/model';
import { useCompany } from '@root/Components/Router/CompanyContent';
import { useDi, useDiContainer } from '@root/Services/DI';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@root/Components/DataGrid';
import styled from '@emotion/styled';
import { addMonths, addWeeks, addYears, set } from 'date-fns';
import { QueryExpr, getAssessmentsGetAssessmentDefinitions, postAssessmentsQueryAssessments, postAssessmentsSearchAll } from '@apis/Resources';
import { LineChart, LineChartSettings } from '@root/Components/Charts/LineChart';
import { Clearfix, TooltipWhite } from '@root/Design/Primitives';
import { AssessmentsHistoryRangePicker, AssessmentsHistoryScoreTypePicker, AssessmentsPlatformSelector } from './Components/AssessmentCommon';
import { Node } from '@root/Components/VirtualTree/Node';
import { postCompanyStatsQueryCompanyStats } from '@apis/Customers';
import { FillerSwitch } from '@root/Design/Filler';
import { AssessmentsPage } from './AssessmentsPage';
import { BaseChartKeySelectionStrategy } from '@root/Components/DataGrid/BaseChartKeySelectionStrategy';
import { useEvent } from '@root/Services/EventEmitter';
import { ChartWrapper } from '@root/Components/Charts/Design';
import { ChartKeySelectionStrategy } from '../Invoices/Models/ChartKeySelectionStrategy';
import { ArrayDataSource } from '@root/Services/Query/ArrayDataSource';

export function MspHistoryPage() {
    const company = useCompany();
    const formatSvc = useDi(FormatService);
    const today = new Date();

    //load data here to feed both time line and grid
    const [defaultCriteria, setDefaultCriteria] = useState<IQueryExpr>({});
    const [startDate, setStartDate] = useState<Date>(addMonths(today, -3));
    const [endDate, setEndDate] = useState<Date>(today);
    const [gridFilters, setGridFilters] = useState<IQueryExpr[]>([]);
    const [type, setType] = useState<string>('All');
    const [lineData, setLineData] = useState<Record<string, number | string>[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [interval, setInterval] = useState<'week' | 'month' | '3month' | 'year'>('3month');
    const [lineInterval, setLineInterval] = useState<'hour' | 'day' | 'month' | `${number}d`>('day');
    const [gridLineData, setGridLineData] = useState<{ filter: IQueryExpr; label: string; color: string }[]>([]);
    const [lineSelection, setLineSelection] = useState<string>('score');
    const [companyList, setCompanyList] = useState<{ CompanyName: string | null | undefined; Id: number }[]>([]);
    const gridSelectionStrategy = useMemo(() => new ChartKeySelectionStrategy(10), []);

    useEffect(() => {
        (async () => {
            const companyList = await queryBuilder<CompanyStat>()
                .take(1000)
                .select((b) => ({
                    CompanyName: b.model.CompanyName,
                    Id: b.model.Id,
                }))
                .execute(postCompanyStatsQueryCompanyStats);
            setCompanyList(companyList.Results ?? []);
        })();
    }, []);

    useEffect(() => {
        let types: string[] = [];
        if (type == 'All') {
            types = ['Aws', 'Azure'];
        } else {
            types = [type];
        }

        const defaultCriteria: IQueryExpr = {
            Operation: 'and',
            Operands: [
                { Operation: 'eq', Operands: [{ field: 'CompanyId' }, { value: companyList.map((x) => x.Id) }] },
                { Operation: 'eq', Operands: [{ field: 'Platform' }, { value: types }] },
                { Operation: 'gte', Operands: [{ field: 'AssessmentDate' }, { value: startDate }] },
                { Operation: 'lte', Operands: [{ field: 'AssessmentDate' }, { value: endDate }] },
            ],
        };
        setDefaultCriteria(defaultCriteria);
    }, [startDate, endDate, type, companyList]);

    useEffect(() => {
        (async () => {
            const where: IQueryExpr = {
                Operation: 'and',
                Operands: [defaultCriteria, ...gridFilters],
            };

            const result = await queryBuilder<{
                AssessmentDate: Date;
                ['AssessmentScore.AssessmentScore']: number;
                ['AssessmentScore.AssessmentSavings']: number;
                ['AssessmentScore.TotalCount']: number;
            }>()
                .where((b) => b.fromExpr(where as QueryExpr))
                .select((b) => ({
                    ...(gridLineData
                        ?.map((line) => ({
                            alias: line.label,
                            expr: b.aggIf(
                                b.and(b.model['AssessmentScore.TotalCount'].gt(0), b.fromExpr(line.filter as QueryExpr)),
                                b.avg(b.model[lineSelection == 'score' ? 'AssessmentScore.AssessmentScore' : 'AssessmentScore.AssessmentSavings'])
                            ),
                        }))
                        .reduce((parent, item) => {
                            return { ...parent, [item.alias]: item.expr };
                        }, {}) ?? {}),
                    Date: b.truncDate(lineInterval, b.model.AssessmentDate!, 0, undefined, undefined) as unknown as number,
                    Total: b.aggIf(
                        b.model['AssessmentScore.TotalCount'].gt(0),
                        lineSelection == 'score'
                            ? b.avg(b.model['AssessmentScore.AssessmentScore'])
                            : b.avg(b.model['AssessmentScore.AssessmentSavings'])
                    ),
                }))
                .execute(postAssessmentsQueryAssessments);

            const items = result.Results ?? [];

            if (gridLineData.length > 0) {
                const flattenedItems: Record<string, number | string>[] = [];
                for (const item of items) {
                    const entries = Object.entries(item);
                    const date = item.Date;
                    for (const [key, value] of entries) {
                        if (key !== 'Date' && key !== 'Total') {
                            flattenedItems.push({ Date: date, Total: value as unknown as number, Label: key });
                        }
                    }
                }

                const lineData = flattenedItems
                    .sort((a, b) => (a.Date as number) - (b.Date as number))
                    .map((o) => ({
                        Date: formatSvc.formatDate(formatSvc.parseDateNoTime(o.Date as string)),
                        Label: o.Label,
                        Total: lineSelection == 'score' ? (o.Total as number) ?? 0 : o.Total ?? (0 as number),
                    }));
                setLineData(lineData);
            } else {
                const lineData = items.map((o) => ({
                    Date: formatSvc.formatDate(formatSvc.parseDateNoTime(o.Date)),
                    Total: o.Total ?? 0,
                }));

                setLineData(lineData);
            }
        })();
    }, [defaultCriteria, gridFilters, lineSelection, gridLineData]);

    //load time line controls here to pass as right place holder and have events at this level.
    function toggleInterval(interval: string) {
        switch (interval) {
            case 'week':
                setInterval('week');
                setStartDate(addWeeks(today, -1));
                setEndDate(today);
                break;
            case 'month':
                setInterval('month');
                setStartDate(addMonths(today, -1));
                setEndDate(today);
                break;
            case '3month':
                setInterval('3month');
                setStartDate(addMonths(today, -3));
                setEndDate(today);
                break;
            case 'year':
                setInterval('year');
                setStartDate(addYears(today, -1));
                setEndDate(today);
                break;
        }
    }

    useEffect(() => {
        switch (interval) {
            case 'week':
                setLineInterval('day');
                break;
            case 'month':
                setLineInterval('day');
                break;
            case '3month':
                setLineInterval('7d');
                break;
            case 'year':
                setLineInterval('month');
                break;
        }
    }, [interval]);

    const rightTopPlaceHolder: ReactNode = (
        <>
            <AssessmentsHistoryRangePicker selectedInterval={interval} setInterval={toggleInterval} />
        </>
    );
    //add logic to determine start date based on lookback range component
    const chartProps = useMemo(
        () => ({
            groups: gridLineData.length > 0 ? ['Date', 'Label'] : ['Date'],
            values: ['Total'],
            settings: {
                chartColors: gridLineData.length > 0 ? gridLineData.map((line) => line.color) : undefined,
                format: lineSelection == 'score' ? 'raw-percent' : 'money-whole',
                margin: { top: 20, right: 20, bottom: 60, left: 60 },
                yMax: lineSelection == 'score' ? 100 : undefined,
                interval: lineInterval,
                noWrapper: true,
            } as LineChartSettings,
        }),
        [lineSelection, lineInterval, gridLineData]
    );

    return (
        <FillerSwitch loading={loading}>
            {() => (
                <AssessmentsPage
                    title={'History'}
                    titleRightPlaceholder={
                        <>
                            <Box sx={{ width: '300px' }}>
                                <AssessmentsPlatformSelector style={{ float: 'right' }} selectedType={type} setType={setType} />
                                <AssessmentsHistoryScoreTypePicker selectedType={lineSelection} setType={setLineSelection} />
                                <Clearfix />
                            </Box>
                        </>
                    }
                >
                    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
                        <Card sx={{ minHeight: '300px', flex: 0 }} shadow="md" withBorder radius="lg">
                            <LineChart data={lineData} {...chartProps} />
                        </Card>
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <MspHistoricalAssessmentGrid
                                criteria={defaultCriteria}
                                setLoading={setLoading}
                                rightTopPlaceHolder={rightTopPlaceHolder}
                                setGridFilters={setGridFilters}
                                setLineData={setGridLineData}
                            />
                        </Box>
                    </Stack>
                </AssessmentsPage>
            )}
        </FillerSwitch>
    );
}

type MspHistoricalAssessmentRow = AssessmentResult & { UfTitle: string; Id: string; CompanyName: string; CompanyId: number; depth: number };

interface MspHistoricalAssessmentGridProps {
    criteria?: IQueryExpr;
    rightTopPlaceHolder?: ReactNode;
    setGridFilters: (filters: IQueryExpr[]) => void;
    setLoading: (loading: boolean) => void;
    setLineData: (data: { filter: IQueryExpr; label: string; color: string }[]) => void;
}
export function MspHistoricalAssessmentGrid({ ...props }: MspHistoricalAssessmentGridProps) {
    const formatSvc = useDi(FormatService);
    const [grid, setGrid] = useState<DataGridModel>();
    const gridModel = useMemo(() => ({ grid: undefined as DataGridModel | undefined }), []);
    const gridSelectionStrategy = useMemo(() => new MspHistoricalLineChartSelectionStrategy(6, true), []);
    const [filters, setFilters] = useState<IQueryExpr[]>([]);
    const [companyList, setCompanyList] = useState<CompanyStat[]>([]);
    const rowSelected = useMemo(() => [] as MspHistoricalAssessmentRow[], []);

    useEffect(() => {
        (async () => {
            const companyList = await queryBuilder<CompanyStat>().take(1000).execute(postCompanyStatsQueryCompanyStats);
            setCompanyList(companyList.Results ?? []);
        })();
    }, []);

    const handleModelLoaded = async (dataGrid: DataGridModel) => {
        setGrid(dataGrid);
        gridModel.grid = dataGrid;
    };

    const onFilterAdded = (filters: IQueryExpr[]) => {
        setFilters(filters);
        props.setGridFilters(filters);
    };

    const onFilterClearing = () => {
        setFilters([]);
        props.setGridFilters([]);
    };

    const columns = useMemo(
        () =>
            [
                {
                    id: 'AssessmentCode',
                    noResize: true,
                    header: 'Assessment',
                    accessor: (item) => item.UfTitle ?? '',
                    filter: {
                        filterType: 'string',
                        name: 'Assessment',
                        filterField: 'AssessmentCode',
                        options: {
                            getValueProvider() {
                                return [
                                    { label: 'EC2 Rightsizing', value: 'EC2 Rightsizing' },
                                    { label: 'RDS Rightsizing', value: 'RDS Rightsizing' },
                                    { label: 'Idle EC2 Resources', value: 'Idle EC2 Resources' },
                                    { label: 'Idle ELB Resources', value: 'Idle ELB Resources' },
                                    { label: 'Idle EMR Resources', value: 'Idle EMR Resources' },
                                    { label: 'Idle RDS Resources', value: 'Idle RDS Resources' },
                                    { label: 'Idle Redshift Resources', value: 'Idle Redshift Resources' },
                                    { label: 'Tag Clarity', value: 'Tag Clarity' },
                                    { label: 'Tag Compliance', value: 'Tag Compliance' },
                                    { label: 'Tag Coverage', value: 'Tag Coverage' },
                                ];
                            },
                        },
                    },
                    sortField: 'UfTitle',
                    align: 'left',
                    defaultWidth: 200,
                    noRemove: true,
                    defaultFixed: true,
                    allowGrouping: true,
                },
                {
                    id: 'AssessmentCategory',
                    noResize: true,
                    header: 'Category',
                    accessor: (item) => item.AssessmentCategory,
                    filter: {
                        filterType: 'string',
                        name: 'Category',
                        filterField: 'AssessmentCategory',
                        options: {
                            getValueProvider() {
                                return [
                                    { label: 'Optimization', value: 'Optimization' },
                                    { label: 'Compliance', value: 'Compliance' },
                                ];
                            },
                        },
                    },
                    sortField: 'AssessmentCategory',
                    align: 'left',
                    defaultWidth: 150,
                    noRemove: true,
                    defaultFixed: true,
                },
                {
                    id: 'AssessmentType',
                    noResize: true,
                    header: 'Type',
                    accessor: (item) => item.AssessmentType,
                    filter: {
                        filterType: 'string',
                        name: 'Type',
                        filterField: 'AssessmentType',
                        options: {
                            getValueProvider() {
                                return [
                                    { label: 'Compute', value: 'Compute' },
                                    { label: 'Database', value: 'Database' },
                                    { label: 'Tagging', value: 'Tagging' },
                                ];
                            },
                        },
                    },
                    sortField: 'AssessmentType',
                    align: 'left',
                    defaultWidth: 150,
                    noRemove: true,
                    defaultFixed: true,
                },
                {
                    id: 'AssessmentDate',
                    noResize: true,
                    header: 'Assessment Date',
                    filter: {
                        filterType: 'date',
                        name: 'Date',
                        filterField: 'AssessmentDate',
                    },
                    cellRenderer: (item) => <div>{formatSvc.formatDate(formatSvc.toLocalDate(new Date(item.AssessmentDate!)))}</div>,
                    accessor: (item) => item.AssessmentDate,
                    sortField: 'AssessmentDate',
                    align: 'left',
                    defaultWidth: 210,
                    noRemove: true,
                    defaultFixed: true,
                },
                {
                    id: 'AssessmentScore.AssessmentScore',
                    noResize: true,
                    header: 'Score',
                    align: 'center',
                    cellRenderer: (item) =>
                        item.AssessmentScore && !item.AssessmentScore?.TotalCount
                            ? 'N/A'
                            : typeof item.AssessmentScore?.AssessmentScore === 'number'
                            ? formatSvc.formatInt(Math.round((item.AssessmentScore?.AssessmentScore ?? 0) * 100))
                            : null,
                    accessor: (item) => item.AssessmentScore?.AssessmentScore ?? 0,
                    sortField: 'AssessmentScore.AssessmentScore',
                    defaultWidth: 100,
                    noRemove: true,
                    defaultFixed: true,
                },
                {
                    id: 'AssessmentScore.AssessmentSavings',
                    noResize: true,
                    header: 'Savings or Risk',
                    align: 'right',
                    cellRenderer: (item) =>
                        typeof item.AssessmentScore?.AssessmentSavings === 'number'
                            ? formatSvc.formatMoneyNoDecimals(item.AssessmentScore?.AssessmentSavings ?? 0)
                            : null,
                    accessor: (item) => item.AssessmentScore?.AssessmentSavings! ?? 0,
                    sortField: 'AssessmentScore.AssessmentSavings',
                    defaultWidth: 190,
                    noRemove: true,
                    defaultFixed: true,
                },
                {
                    id: 'AssessmentScore.AssessmentOpportunityCount',
                    noResize: true,
                    header: 'Opportunities',
                    align: 'right',
                    cellRenderer: (item) =>
                        typeof item.AssessmentScore?.AssessmentOpportunityCount === 'number'
                            ? formatSvc.formatMoneyNoDecimals(item.AssessmentScore?.AssessmentOpportunityCount ?? 0)
                            : null,
                    accessor: (item) => item.AssessmentScore?.AssessmentOpportunityCount! ?? 0,
                    sortField: 'AssessmentScore.AssessmentOpportunityCount',
                    defaultWidth: 150,
                    noRemove: true,
                    defaultFixed: true,
                },
                {
                    id: 'CompanyName',
                    header: 'Company Name',
                    filter: {
                        filterType: 'string',
                        name: 'Company Name',
                        filterField: 'CompanyName',
                        options: {
                            getValueProvider() {
                                return companyList.map((x) => ({
                                    label: x.CompanyName ?? '',
                                    value: x.CompanyName ?? '',
                                }));
                            },
                        },
                    },
                    hideInColumnSelector: true,
                },
            ] as ColumnConfig<MspHistoricalAssessmentRow>[],
        [companyList]
    );

    const defaultGroupBy = useMemo(
        () =>
            [
                { id: 'CompanyName', sortDir: 'Desc', sortMode: 'count' },
                { id: 'AssessmentCode', sortDir: 'Desc', sortMode: 'count' },
            ] as GridGroupByState[],
        []
    );

    const gridDataSource = useMemo(() => {
        return {
            async getPage(start, end, state, parent) {
                const { builder: xb } = exprBuilder<MspHistoricalAssessmentRow>();
                if (parent == null || parent == undefined) {
                    const validAssmFilters: IQueryExpr[] = [];
                    const validCompanyFilters: IQueryExpr[] = [];
                    state.filters.forEach((x) => {
                        let foundCompanyName = false;
                        traverseExpr(x as QueryExpr, (cx) => {
                            foundCompanyName = foundCompanyName || ('Field' in cx && cx.Field == 'CompanyName');
                        });
                        if (foundCompanyName) {
                            validCompanyFilters.push(x);
                        } else {
                            validAssmFilters.push(x);
                        }
                    });
                    const uniqueCompanies = await queryBuilder<MspHistoricalAssessmentRow>()
                        .where((b) =>
                            xb.fromExpr<boolean>({
                                Operation: 'and',
                                Operands: [...(validAssmFilters ?? []), props.criteria!].filter((x) => !!x),
                            })
                        )
                        .select((b) => ({
                            CompanyId: b.model.CompanyId,
                            Ct: b.count(),
                        }))
                        .execute(postAssessmentsQueryAssessments);
                    const companyIds = (uniqueCompanies.Results ?? []).reduce((result, item) => result.add(item.CompanyId ?? 0), new Set<number>());
                    let results = companyList
                        .filter((c) => companyIds.has(c.Id ?? 0))
                        .map(
                            (x) =>
                                ({
                                    UfTitle: x.CompanyName,
                                    CompanyName: x.CompanyName!,
                                    CompanyId: x.Id!,
                                    depth: 0,
                                } as MspHistoricalAssessmentRow)
                        );
                    const items = new ArrayDataSource(results).applyState({ ...state, filters: validCompanyFilters });

                    return { items, total: items.length };
                } else if (parent.depth == 0) {
                    const results = await getAssessmentsGetAssessmentDefinitions();
                    const groupCrit = [xb.resolve(xb.model.CompanyId!.eq(parent.CompanyId))];
                    const validAssessmentDefResults = await queryBuilder<MspHistoricalAssessmentRow>()
                        .where((b) =>
                            xb.fromExpr<boolean>({
                                Operation: 'and',
                                Operands: [...(state.filters ?? []), props.criteria!, ...groupCrit].filter((x) => !!x),
                            })
                        )
                        .select((b) => ({
                            AssessmentDefId: b.model.AssessmentDefId,
                            CompanyId: b.model.CompanyId,
                            Ct: b.count(),
                        }))
                        .execute(postAssessmentsQueryAssessments);

                    const validAssessmentDefs = (validAssessmentDefResults?.Results ?? []).reduce(
                        (set, item) => set.add(item.AssessmentDefId ?? ''),
                        new Set<string>()
                    );

                    let items = results
                        .filter((x) => validAssessmentDefs.has(x.Id ?? ''))
                        .map((x) => ({ ...x, CompanyName: parent.CompanyName, depth: 1, CompanyId: parent.CompanyId } as MspHistoricalAssessmentRow));

                    return { items: items, total: items.length };
                } else {
                    const query = queryBuilder<MspHistoricalAssessmentRow>()
                        .take(end - start + 1)
                        .skip(start)
                        .sortAsc((b) => b.model.AssessmentDate!)
                        .build();
                    const groupCrit = [xb.resolve(xb.model.AssessmentDefId!.eq(parent.Id)), xb.resolve(xb.model.CompanyId!.eq(parent.CompanyId))];
                    query.Where = { Operation: 'and', Operands: [...(state.filters ?? []), props.criteria, ...groupCrit] };
                    const results = await postAssessmentsSearchAll(query);
                    let items = results.Results!.map((x) => ({ ...x, depth: 2 } as MspHistoricalAssessmentRow));

                    return { items: items, total: results.Count };
                }
            },
        } as DataSourceConfig<MspHistoricalAssessmentRow>;
    }, [props.criteria, filters]);
    const rowSelector = useRowSelector({ gridSelectionStrategy, tooltip: 'Chart this data in Historical Chart' });
    const handleSelectionChanged = useCallback(
        async ({ getItems }: { getItems: () => Promise<MspHistoricalAssessmentRow[]> }) => {
            const selections = gridSelectionStrategy.getSelectedIds();
            const items = companyList.filter((s) => selections.includes(s.Id ?? 0));
            const lineItems = items.map((s) => ({
                filter: { Operation: 'eq', Operands: [{ field: 'CompanyId' }, { value: s.Id }] } as IQueryExpr,
                color: gridSelectionStrategy.getSelectionColorByKey(s.Id ?? 0)!,
                label: s.CompanyName ?? '',
            }));
            props.setLineData(lineItems);
        },
        [companyList]
    );
    const onRowClick = useCallback(async (row: MspHistoricalAssessmentRow) => {
        if (row.depth === 0) {
            gridModel.grid?.setSelected(row, !gridModel.grid?.isSelected(row));
        }
    }, []);

    const childAccessor = useMemo(() => {
        return {
            hasChildren: (row: MspHistoricalAssessmentRow) => {
                if (row.depth === 0) {
                    return 'CompanyName' in row;
                } else if (row.depth === 1) {
                    return 'Id' in row;
                } else if (row.depth === 2) {
                    return false;
                }
            },
        };
    }, [grid]);

    return (
        <>
            {companyList.length > 0 && (
                <DataGrid
                    dataSource={gridDataSource}
                    columns={columns}
                    defaultGroupBy={defaultGroupBy}
                    groupByAsRows={true}
                    onFilterAdded={onFilterAdded}
                    onFilterClearing={onFilterClearing}
                    rightTopPlaceHolder={props.rightTopPlaceHolder}
                    onModelLoaded={handleModelLoaded}
                    selectionMode="multiple"
                    onSelectedChanged={handleSelectionChanged}
                    selectionStrategy={gridSelectionStrategy}
                    renderRowSelector={rowSelector}
                    onRowClick={onRowClick}
                    showRefresh
                    childAccessor={childAccessor}
                    allowSavedViews={true}
                    hideGlobalSearch
                    statePersistence={{ key: `MSP-Assessment-History` }}
                    exportName="msp-Historical-Assessments"
                />
            )}
        </>
    );
}

class MspHistoricalLineChartSelectionStrategy extends BaseChartKeySelectionStrategy<MspHistoricalAssessmentRow, number> {
    constructor(maxSelections: number, allSelected: boolean) {
        super(maxSelections, allSelected, (item) => item.CompanyId);
    }
}

function MspHistoricalLineKeyRowSelector({
    selectionStrategy,
    item,
    toggle,
    color,
    tooltip,
    isHeader,
}: {
    selectionStrategy: MspHistoricalLineChartSelectionStrategy;
    item: MspHistoricalAssessmentRow | null;
    toggle: (selected: boolean) => void;
    color?: string;
    tooltip?: string;
    isHeader: boolean;
}) {
    const theme = useMantineTheme();
    useEvent(selectionStrategy.selectionChanged);
    const isSelected = item ? selectionStrategy.isSelected(item) : false;
    const itemColor = item ? selectionStrategy.getSelectionColor(item) : null;
    const allSelected = selectionStrategy.isAllSelected();
    const selectAll = useCallback(() => {
        toggle(true);
    }, [selectionStrategy, toggle]);
    const iconColor = allSelected ? theme.colors.primary[6] : isSelected ? color ?? itemColor ?? theme.colors.primary[6] : theme.colors.gray[4];
    return item?.depth !== 0 && !isHeader ? null : (
        <Tooltip withinPortal label={tooltip} position="right">
            {item ? (
                <i className="ti ti-chart-histogram" style={{ color: iconColor }} />
            ) : (
                <i className="ti ti-chart-histogram" style={{ color: theme.colors.primary[6] }} onClick={selectAll} />
            )}
        </Tooltip>
    );
}

function useRowSelector({
    gridSelectionStrategy,
    color,
    tooltip,
}: {
    gridSelectionStrategy: MspHistoricalLineChartSelectionStrategy;
    color?: string;
    tooltip?: string;
}) {
    return useCallback(
        (item: MspHistoricalAssessmentRow | null, { toggle }: { toggle: (selected: boolean) => void }, isHeader: boolean) => {
            tooltip = isHeader ? 'Chart all data' : tooltip;
            return (
                <MspHistoricalLineKeyRowSelector
                    selectionStrategy={gridSelectionStrategy}
                    toggle={toggle}
                    item={item}
                    color={color}
                    tooltip={tooltip}
                    isHeader={isHeader}
                />
            );
        },
        [gridSelectionStrategy, color, tooltip]
    );
}
